/**
 * The work orders, material orders and other expenses, for the close-out
 * pop-up — ADR 0273.
 *
 * Three of the five questions: "are the work orders accurate and complete,
 * are the material orders accurate, are the other expenses accurate and
 * complete". Each order is a line of what it says, then what might be wrong
 * with it.
 */

import { billFor, billNote, billTrouble, section } from "./closeoutChecks.js";
import type {
  CloseoutInput,
  CloseoutOption,
  CloseoutSection,
} from "./closeout.js";
import { documentTotal, type LadderTotals } from "./ladder.js";
import { cents, formatMoney, toCents } from "./money.js";
import {
  isPayable,
  payoutState,
  payoutTotal,
  type PayoutState,
} from "./payout.js";
import { receiptNamesCustomer } from "./receiptCustomer.js";
import { tradeNames } from "./tradeProgress.js";

const WORK_STATUS: Record<string, string> = {
  not_scheduled: "not scheduled",
  scheduled: "scheduled",
  completed: "complete",
  paid: "paid",
};

const MATERIAL_STATUS: Record<string, string> = {
  not_placed: "not placed",
  ordered: "ordered",
  picked_up: "picked up",
  paid: "paid",
};

const PAYOUT_STATE: Record<PayoutState, string> = {
  not_ready: "payout not ready",
  needs_review: "payout not approved",
  approved: "payout approved, not paid",
  paid: "payout paid",
  rejected: "payout rejected",
  held: "payout on hold",
  sent: "payout billed",
};

/** What the payout review says about a payout, when there is something. */
const PAYOUT_WARNING: Partial<Record<PayoutState, string>> = {
  needs_review: "the payout has not been approved.",
  approved: "the payout is approved but not paid.",
  rejected: "the payout was rejected.",
  held: "the payout is on hold.",
};

/** States in which QuickBooks should have the payout as a bill. */
const BILLED: ReadonlySet<PayoutState> = new Set(["approved", "paid", "sent"]);

/* --------------------------------------------------------- work orders */

type WorkOrderRow = CloseoutOption["workOrders"][number];

/** The payout columns with every absent one read as null. */
function payoutColumns(wo: WorkOrderRow) {
  return {
    ...wo,
    completed_at: wo.completed_at ?? null,
    payout_approved_at: wo.payout_approved_at ?? null,
    payout_rejected_at: wo.payout_rejected_at ?? null,
    payout_held_at: wo.payout_held_at ?? null,
    payout_sent_at: wo.payout_sent_at ?? null,
    payout_paid_at: wo.payout_paid_at ?? null,
    payout_paid_manually_at: wo.payout_paid_manually_at ?? null,
    payout_amount: wo.payout_amount ?? null,
  };
}

function workOrderLine(
  out: CloseoutSection,
  wo: WorkOrderRow,
  input: CloseoutInput,
): void {
  const what = `Work order #${wo.order_number}`;
  const payable = isPayable(wo);
  const columns = payoutColumns(wo);
  const state = payoutState(columns);
  const bill = billFor(input.quickbooks, "crew_payout", wo.id);
  const pays = payable ? payoutTotal(columns) : documentTotal(wo);
  const trades = tradeNames(wo.trades).join(", ") || "no trade";
  const status = WORK_STATUS[wo.status] ?? wo.status;
  const payout = payable ? PAYOUT_STATE[state] : "in-house";
  out.facts.push(
    `${what} (${trades}): ${wo.crew?.name ?? "no crew"}, ${status}, ${formatMoney(pays)}, ${payout}${billNote(bill)}`,
  );

  if (!wo.crew_id) out.warnings.push(`${what} has no crew.`);
  const complete = wo.status === "completed" || wo.status === "paid";
  if (!complete) out.warnings.push(`${what} is not marked complete.`);
  const review = PAYOUT_WARNING[state];
  if (payable && complete && review) out.warnings.push(`${what}: ${review}`);
  const trouble = billTrouble(bill, what, payable && BILLED.has(state));
  if (trouble) out.warnings.push(trouble);
}

export function workOrdersSection(input: CloseoutInput): CloseoutSection {
  const out = section("work_orders");
  const orders = input.options.flatMap((o) => o.workOrders);
  if (input.job.work_orders_internal)
    out.facts.push("Work orders handled internally.");
  else if (orders.length === 0)
    out.warnings.push("There are no work orders on this job.");
  for (const wo of orders) workOrderLine(out, wo, input);
  return out;
}

/* ----------------------------------------------------- material orders */

type MaterialOrderRow = CloseoutOption["materialOrders"][number];

function materialOrderLine(
  out: CloseoutSection,
  mo: MaterialOrderRow,
  input: CloseoutInput,
): void {
  const what = `Material order #${mo.order_number}`;
  const bill = billFor(input.quickbooks, "material_order", mo.id);
  const trade = mo.trade?.trim() || "no trade";
  const status = MATERIAL_STATUS[mo.status] ?? mo.status;
  const checked = mo.reconciled_at
    ? "checked against the bill"
    : "not checked against the bill";
  out.facts.push(
    `${what} (${trade}): ${mo.supplier?.name ?? "no supplier"}, ${status}, ${formatMoney(documentTotal(mo))}, ${checked}${billNote(bill)}`,
  );

  if (mo.status === "not_placed")
    out.warnings.push(`${what} was never placed.`);
  if (mo.status === "ordered")
    out.warnings.push(`${what} is not marked picked up or delivered.`);
  if (!mo.reconciled_at)
    out.warnings.push(
      `${what} has not been checked against the supplier's bill.`,
    );
  // A material bill is raised when the material arrives (ADR 0088).
  const arrived = mo.status === "picked_up" || mo.status === "paid";
  const trouble = billTrouble(bill, what, arrived);
  if (trouble) out.warnings.push(trouble);
}

export function materialOrdersSection(input: CloseoutInput): CloseoutSection {
  const out = section("material_orders");
  const orders = input.options.flatMap((o) => o.materialOrders);
  if (input.job.materials_internal)
    out.facts.push("Materials handled internally.");
  if (input.job.material_by_crew)
    out.facts.push("The crew provided the material.");
  const noneNeeded = input.job.materials_internal || input.job.material_by_crew;
  if (orders.length === 0 && !noneNeeded)
    out.warnings.push("There are no material orders on this job.");
  for (const mo of orders) materialOrderLine(out, mo, input);
  return out;
}

/* ------------------------------------------------------ other expenses */

type ExpenseRow = CloseoutOption["expenses"][number];

function expenseLine(
  out: CloseoutSection,
  ex: ExpenseRow,
  input: CloseoutInput,
): void {
  const what = `Expense "${ex.name?.trim() || "unnamed"}"`;
  const bill = billFor(input.quickbooks, "job_expense", ex.id);
  const how = ex.payment_type ? `, ${ex.payment_type}` : "";
  const receipt = ex.document_url ? ", receipt attached" : ", no receipt";
  out.facts.push(
    `${what}: ${formatMoney(toCents(ex.amount))}${how}${receipt}${billNote(bill)}`,
  );
  if (!ex.document_url) out.warnings.push(`${what} has no receipt attached.`);
  // Not every expense is billed — a processing fee never is — so only a bill
  // that exists and is in trouble is worth a line.
  const trouble = billTrouble(bill, what, false);
  if (trouble) out.warnings.push(trouble);
}

/** What a receipt says about itself: vendor, amount and date. */
function receiptText(r: {
  vendor: string | null;
  amountCents: number | null;
  txnDate: string | null;
}): string {
  const amount =
    r.amountCents == null ? "no amount" : formatMoney(cents(r.amountCents));
  const date = r.txnDate ? `, ${r.txnDate}` : "";
  return `${r.vendor ?? "unknown vendor"}, ${amount}${date}`;
}

function receiptLines(out: CloseoutSection, input: CloseoutInput): void {
  for (const r of input.quickbooks.receipts)
    out.facts.push(`QuickBooks receipt: ${receiptText(r)}`);
  // A receipt accepted in QuickBooks without a project waits in the receipts
  // list with no customer on it; its note is the only thing that says whose
  // it is (issue #899). One naming this customer is a cost not on the job yet.
  for (const r of input.quickbooks.waitingReceipts ?? []) {
    if (!receiptNamesCustomer(r, input.job.customer)) continue;
    const note = r.description?.trim() ? `, "${r.description.trim()}"` : "";
    out.warnings.push(
      `A QuickBooks receipt naming this customer is not on any job yet: ${receiptText(r)}${note}.`,
    );
  }
}

export function expensesSection(
  input: CloseoutInput,
  ladders: readonly LadderTotals[],
): CloseoutSection {
  const out = section("expenses");
  for (const ex of input.options.flatMap((o) => o.expenses))
    expenseLine(out, ex, input);
  for (const auto of ladders.flatMap((l) => l.autoExpenses))
    if (!auto.removed)
      out.facts.push(`${auto.name}: ${formatMoney(auto.amount)} (calculated)`);
  receiptLines(out, input);
  if (out.facts.length === 0) out.facts.push("No other expenses.");
  return out;
}
